/**
 * 認証API実サービス（httpOnly Cookie認証方式）
 *
 * @file auth.service.ts
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * バックエンド認証APIとの実際の通信を行うサービス。
 * /api/v1/auth/* エンドポイントと連携。
 * httpOnly Cookie方式を採用し、localStorage使用を完全廃止。
 */

import {
  LoginRequest,
  MultiLanguageText,
  RegisterRequest,
  AdminLoginRequest,
  SellerLoginRequest,
  LoginResponse,
  AdminLoginResponse,
  SellerLoginResponse,
  SocialLoginResponse,
  User,
  AdminUser,
  UserRole,
  Language
} from '@/types';
import { safeFetch } from '@shared/utils/api';
import { getCsrfTokenFromCookie, getCsrfToken } from '@shared/utils/csrf';

// APIベースURL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const AUTH_BASE_URL = `${API_BASE_URL}/api/v1/buyer/auth`;

// バックエンドレスポンス型定義（backend/app/schemas/に対応）
interface BackendTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface BackendUserProfileResponse {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  language: string;
  phone?: string;
  is_verified?: boolean;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

// バックエンド→フロントエンド型変換
const convertBackendUserToFrontend = (backendUser: BackendUserProfileResponse): User => {
  return {
    id: backendUser.id,
    email: backendUser.email,
    firstName: backendUser.first_name,
    lastName: backendUser.last_name,
    role: backendUser.role as UserRole,
    language: (backendUser.language as Language) || 'ja',
    phoneNumber: backendUser.phone,
    isActive: backendUser.is_active,
    emailVerified: backendUser.is_verified,
    createdAt: new Date(backendUser.created_at),
    updatedAt: new Date(backendUser.updated_at)
  };
};

// HTTPヘッダー生成（同期版：GETメソッド用）
function getHeaders(): HeadersInit;
// HTTPヘッダー生成（非同期版：POST/PUT/DELETEメソッド用、CSRF対応）
function getHeaders(withCsrf: boolean): Promise<HeadersInit>;
function getHeaders(withCsrf = false): HeadersInit | Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // 認証はhttpOnly Cookieで自動送信されるため、Authorizationヘッダーは不要

  // CSRF対応が必要な場合は非同期で処理
  if (withCsrf) {
    return (async () => {
      const csrfToken = getCsrfTokenFromCookie();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      } else {
        // Cookieにない場合、APIから取得
        const token = await getCsrfToken("buyer");
        headers['X-CSRF-Token'] = token;
      }
      return headers;
    })();
  }

  return headers;
}


export const authService = {
  /**
   * Buyerログイン（一般購入者）
   * POST /api/v1/auth/login
   */
  loginBuyer: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await safeFetch<BackendTokenResponse>(`${AUTH_BASE_URL}/login`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
        rememberMe: credentials.remember
      }),
      errorMessage: 'ログインに失敗しました。メールアドレスとパスワードを確認してください。'
    });

    // トークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    // ユーザー情報を取得
    const user = await authService.getCurrentUser();

    return {
      user,
      accessToken: response.access_token, // UIでの表示用（実際の認証には使用しない）
      refreshToken: response.refresh_token,
      expiresIn: 3600
    };
  },

  /**
   * 後方互換性のための汎用ログイン（loginBuyerのエイリアス）
   * @deprecated Use loginBuyer() instead
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    return authService.loginBuyer(credentials);
  },

  /**
   * Sellerログイン（販売者）
   * POST /api/v1/seller/auth/login
   */
  loginSeller: async (credentials: SellerLoginRequest): Promise<SellerLoginResponse> => {
    const data = await safeFetch<{
      seller: {
        id: string;
        email: string;
        business_name?: MultiLanguageText;
        business_registration_number?: string;
        is_active: boolean;
        is_verified: boolean;
        status: string;
        created_at: string;
      };
      access_token: string;
      token_type: string;
      role: string;
    }>(`${API_BASE_URL}/api/v1/seller/auth/login`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password
      }),
      errorMessage: 'Sellerログインに失敗しました。'
    });

    // トークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    return {
      seller: {
        id: data.seller.id,
        email: data.seller.email,
        businessName: data.seller.business_name,
        status: data.seller.status,
        commissionRate: 0, // バックエンドレスポンスに存在しないためデフォルト値
        role: UserRole.SELLER
      },
      accessToken: data.access_token,
      refreshToken: '', // バックエンドレスポンスに存在しないため空文字
      expiresIn: 3600
    };
  },

  /**
   * 管理者ログイン
   * POST /api/v1/admin/auth/login
   */
  adminLogin: async (credentials: AdminLoginRequest): Promise<AdminLoginResponse> => {
    const data = await safeFetch<BackendTokenResponse & { requires_2fa?: boolean }>(`${API_BASE_URL}/api/v1/admin/auth/login`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password
      }),
      errorMessage: '管理者ログインに失敗しました。'
    });

    // トークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    // 管理者情報をAPI経由で取得
    const adminUser = await authService.getCurrentAdminUser();

    return {
      user: adminUser,
      accessToken: data.access_token, // UIでの表示用
      refreshToken: data.refresh_token,
      requiresTwoFactor: data.requires_2fa || false,
      expiresIn: 3600
    };
  },

  /**
   * Buyer登録（一般購入者）
   * POST /api/v1/auth/register
   */
  registerBuyer: async (data: RegisterRequest): Promise<{ success: boolean; message: string; emailSent: boolean }> => {
    // バックエンドはemailとpasswordのみ受け付ける
    interface UserRegisterBackendResponse {
      id: string;
      email: string;
      verification_email_sent: boolean;
      message: string;
    }

    const response = await safeFetch<UserRegisterBackendResponse>(`${AUTH_BASE_URL}/register`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        email: data.email,
        password: data.password
      }),
      errorMessage: 'Buyer登録に失敗しました。'
    });

    // 登録成功を返す（自動ログインは行わない - メール確認完了後に手動ログイン）
    return {
      success: true,
      message: '登録が完了しました。確認メールをご確認ください。',
      emailSent: response.verification_email_sent
    };
  },

  /**
   * 後方互換性のための汎用登録（registerBuyerのエイリアス）
   * @deprecated Use registerBuyer() instead
   */
  register: async (data: RegisterRequest): Promise<{ success: boolean; message: string; emailSent: boolean }> => {
    return authService.registerBuyer(data);
  },

  /**
   * ログアウト
   * POST /api/v1/auth/logout
   */
  logout: async (): Promise<void> => {
    try {
      await safeFetch<void>(`${AUTH_BASE_URL}/logout`, {
        method: 'POST',
        headers: await getHeaders(true),
        errorMessage: 'ログアウト処理でエラーが発生しました。'
      });
    } catch (error) {
      console.warn('ログアウトAPIエラー:', error);
    }
    // httpOnly Cookieはバックエンドで削除されるため、フロントエンドでの削除処理は不要
  },

  /**
   * 現在のユーザー情報取得
   * GET /api/v1/auth/profile
   */
  getCurrentUser: async (signal?: AbortSignal): Promise<User | AdminUser> => {
    const backendUser = await safeFetch<BackendUserProfileResponse>(`${AUTH_BASE_URL}/profile`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: 'ユーザー情報の取得に失敗しました。',
      signal,
    });

    return convertBackendUserToFrontend(backendUser);
  },

  /**
   * 現在の管理者情報取得
   * GET /api/v1/admin/auth/profile
   */
  getCurrentAdminUser: async (signal?: AbortSignal): Promise<AdminUser> => {
    const adminData = await safeFetch<BackendUserProfileResponse & { twofa_enabled?: boolean }>(`${API_BASE_URL}/api/v1/admin/auth/profile`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '管理者情報の取得に失敗しました。',
      signal,
    });

    if (adminData.is_active === undefined || adminData.is_active === null) {
      throw new Error('管理者のアクティブ状態（is_active）が必須ですが取得できませんでした。');
    }

    return {
      id: adminData.id,
      email: adminData.email,
      firstName: adminData.first_name,
      lastName: adminData.last_name,
      role: UserRole.ADMIN,
      language: 'ja',
      isActive: adminData.is_active,
      createdAt: new Date(adminData.created_at),
      updatedAt: new Date(adminData.updated_at),
      permissions: [],
      twoFactorEnabled: adminData.twofa_enabled !== undefined ? adminData.twofa_enabled : false,
      failedLoginAttempts: 0
    };
  },

  /**
   * 現在の販売者情報取得
   * GET /api/v1/seller/auth/profile
   */
  getCurrentSellerUser: async (signal?: AbortSignal): Promise<User> => {
    const sellerData = await safeFetch<{
      id: string;
      email: string;
      business_name?: { ja?: string; en?: string; ko?: string };
      business_registration_number?: string;
      is_active: boolean;
      is_verified: boolean;
      status: string;
      created_at: string;
    }>(`${API_BASE_URL}/api/v1/seller/auth/profile`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '販売者情報の取得に失敗しました。',
      signal,
    });

    // Seller情報をUserオブジェクト形式に変換
    return {
      id: sellerData.id,
      email: sellerData.email,
      firstName: (sellerData.business_name?.ja || sellerData.business_name?.en || ''),
      lastName: '',
      role: UserRole.SELLER,
      language: 'ja' as Language,
      isActive: sellerData.is_active,
      status: sellerData.status as 'active' | 'suspended' | 'banned' | 'pending_verification' | 'pending_approval' | undefined,
      createdAt: new Date(sellerData.created_at),
      updatedAt: new Date(sellerData.created_at)
    };
  },

  /**
   * トークンリフレッシュ
   * POST /api/v1/auth/refresh
   */
  refreshToken: async (): Promise<{ accessToken: string; expiresIn: number }> => {
    // httpOnly Cookieのrefresh_tokenはバックエンドで自動的に送信される
    const tokenData = await safeFetch<BackendTokenResponse>(`${AUTH_BASE_URL}/refresh`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({}), // リフレッシュトークンはCookieから自動取得
      errorMessage: 'トークンのリフレッシュに失敗しました。再ログインしてください。'
    });

    // 新しいトークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    return {
      accessToken: tokenData.access_token, // UIでの表示用
      expiresIn: 3600
    };
  },

  /**
   * パスワードリセット要求
   * POST /api/v1/auth/reset-password
   */
  resetPassword: async (email: string): Promise<void> => {
    await safeFetch<void>(`${AUTH_BASE_URL}/reset-password`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ email }),
      errorMessage: 'パスワードリセットの要求に失敗しました。'
    });
  },

  /**
   * パスワード変更（リセットトークン使用）
   * POST /api/v1/buyer/auth/change-password
   */
  changePassword: async (token: string, newPassword: string): Promise<void> => {
    await safeFetch<void>(`${AUTH_BASE_URL}/change-password`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ token, newPassword }),
      errorMessage: 'パスワードの変更に失敗しました。'
    });
  },

  /**
   * 認証状態確認（新規追加）
   * GET /api/v1/auth/me
   */
  checkAuth: async (): Promise<User | AdminUser> => {
    const backendUser = await safeFetch<BackendUserProfileResponse>(`${AUTH_BASE_URL}/me`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '認証状態の確認に失敗しました。'
    });

    return convertBackendUserToFrontend(backendUser);
  },

  /**
   * Googleソーシャルログイン
   * POST /api/v1/auth/social/google
   * @param idToken Google id_token（フロントエンドで取得）
   */
  loginWithGoogle: async (idToken: string): Promise<SocialLoginResponse> => {
    const response = await safeFetch<SocialLoginResponse>(`${API_BASE_URL}/api/v1/auth/social/google`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        provider: 'google',
        idToken: idToken
      }),
      errorMessage: 'Googleログインに失敗しました。'
    });

    return response;
  }
};
